import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BedDouble, Bath, Sofa, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../hooks/use-toast";
import AuthModal from "./AuthModal";

export const formatPrice = (property) => {
  if (property.status === "to-rent") {
    return `\u00A3${property.price.toLocaleString()}`;
  }
  return `\u00A3${property.price.toLocaleString()}`;
};

const PropertyCard = ({ property }) => {
  const isRent = property.status === "to-rent";
  const { user, isFavourite, toggleFavourite } = useAuth();
  const { toast } = useToast();
  const [authOpen, setAuthOpen] = useState(false);
  const faved = isFavourite ? isFavourite(property.id) : false;

  const handleFav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      setAuthOpen(true);
      return;
    }
    toggleFavourite(property.id);
    toast({ title: faved ? "Removed from saved homes" : "Saved to your homes" });
  };

  return (
    <Link
      to={`/property/${property.slug}`}
      className="group block bg-white rounded-xl overflow-hidden border border-[#f0ece5] hover:shadow-[0_16px_50px_rgba(0,0,0,0.12)] transition-all duration-300"
    >
      <div className="relative overflow-hidden aspect-[4/3]">
        <img
          src={property.images[0]}
          alt={property.title}
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
          loading="lazy"
        />
        <span className="absolute top-4 left-4 px-3 py-1.5 text-[11px] font-500 tracking-[0.12em] uppercase text-white bg-[#b8935f] rounded-full">
          {isRent ? "To Let" : "For Sale"}
        </span>
        <button
          onClick={handleFav}
          aria-label="Save property"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors z-10"
        >
          <Heart size={17} className={faved ? "text-[#b8935f] fill-[#b8935f]" : "text-[#6a6a6a]"} />
        </button>
        <AuthModal open={authOpen} onOpenChange={setAuthOpen} defaultMode="login" />
      </div>
      <div className="p-5">
        <p className="text-[12px] tracking-[0.1em] uppercase text-[#b8935f] mb-1.5">{property.type}</p>
        <h3 className="font-display text-[19px] text-[#2b2b2b] font-500 mb-2 group-hover:text-[#b8935f] transition-colors">
          {property.title}
        </h3>
        <p className="text-[13px] text-[#8a8a8a] mb-4">{property.priceQualifier}</p>
        <p className="font-display text-[22px] text-[#2b2b2b] font-500 mb-4">
          {formatPrice(property)}
          {isRent && <span className="text-[13px] text-[#8a8a8a] font-400 ml-1">pcm</span>}
        </p>
        <div className="flex items-center gap-5 pt-4 border-t border-[#f0ece5] text-[#6a6a6a]">
          <span className="flex items-center gap-1.5 text-[13px]">
            <BedDouble size={17} className="text-[#b8935f]" /> {property.bedrooms}
          </span>
          <span className="flex items-center gap-1.5 text-[13px]">
            <Bath size={17} className="text-[#b8935f]" /> {property.bathrooms}
          </span>
          {property.receptions > 0 && (
            <span className="flex items-center gap-1.5 text-[13px]">
              <Sofa size={17} className="text-[#b8935f]" /> {property.receptions}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default PropertyCard;
